import { getDataOrNull } from "@msys/common";
import { StatusChip } from "@msys/ui";
import { Button, Tooltip, useTheme } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { namedOperations } from "../../../clients/graphqlTypes.js";
import { integrationErrorMessage } from "../../main-routes/integrations/integrationErrorMessage.js";
import { useUserData } from "../../auth/useUserData.js";
import { PdsIcon } from "./PdsIcon.js";
import {
  useExportQuoteToPdsMutation,
  usePdsQuoteExportStatusQuery,
} from "./PdsQuoteExport.generated.js";

const PENDING_POLL_INTERVAL_MS = 5_000;

// Tinted "exported" chip — mirrors the PDS health "ok" colour used on the
// settings status header so the same success state reads the same everywhere.
const EXPORTED_CHIP = { bg: "#e8f5e9", text: "#2e7d32" };

/** Renders only for organisations with a connected pds_sync integration —
 * non-PDS organisations perform no per-quote request (design D8). */
export function useHasPdsSyncIntegration(): boolean {
  const userData = useUserData();
  if (!userData.currentUser) return false;
  return userData.currentUser.organisation.enabledIntegrationIds.includes(
    "pds_sync"
  );
}

interface Props {
  projectId: string;
  quoteId: string;
  isPublished: boolean;
}

/**
 * "Export to PDS" action on the published-quote view (MEIS-12747).
 * States per spec pds-quote-export "Export status query": hidden for
 * NOT_CONNECTED/BLOCKED, disabled-with-reason for CUSTOMER_NOT_LINKED,
 * disabled for unpublished, EXECUTED shows the PDS Vorgangsnummer as a chip.
 */
export const PdsQuoteMenuButton = ({
  projectId,
  quoteId,
  isPublished,
}: Props) => {
  const { t } = useTranslate(["Integrations", "Global"]);
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const hasPdsSync = useHasPdsSyncIntegration();

  const [isPolling, setIsPolling] = React.useState(false);
  const { data, loading } = usePdsQuoteExportStatusQuery({
    variables: { projectId, docId: quoteId },
    skip: !hasPdsSync,
    fetchPolicy: "cache-and-network",
    // an in-flight export resolves asynchronously in the task workflow —
    // poll until it flips to EXECUTED or FAILED
    pollInterval: isPolling ? PENDING_POLL_INTERVAL_MS : undefined,
    onCompleted: result => {
      setIsPolling(
        getDataOrNull(result.pdsQuoteExportStatus)?.status === "PENDING"
      );
    },
  });
  const state = getDataOrNull(data?.pdsQuoteExportStatus);

  const [exportQuote, { loading: exporting }] = useExportQuoteToPdsMutation({
    // Scrub errors ourselves (below) — suppress the global handler so a
    // ClientError request/response dump never reaches the snackbar.
    context: { noErrorNotification: true },
    refetchQueries: [namedOperations.Query.PdsQuoteExportStatus],
    awaitRefetchQueries: true,
  });

  if (!hasPdsSync || !state) return null;
  if (state.status === "NOT_CONNECTED" || state.status === "BLOCKED")
    return null;

  const handleExport = async () => {
    try {
      const result = await exportQuote({
        variables: { input: { projectId, docId: quoteId } },
      });
      const status = result.data?.exportQuoteToPds.status;
      if (status === "PENDING") {
        enqueueSnackbar(
          t("Quote export to PDS started.", { ns: "Integrations" }),
          { variant: "success" }
        );
      }
    } catch (error) {
      enqueueSnackbar(
        integrationErrorMessage(
          error,
          t("Something went wrong. Please try again.", { ns: "Global" })
        ),
        { variant: "error" }
      );
    }
  };

  if (state.status === "EXECUTED") {
    return (
      <StatusChip
        size="small"
        backgroundColor={EXPORTED_CHIP.bg}
        textColor={EXPORTED_CHIP.text}
        label={t("Exported to PDS: {number}", {
          ns: "Integrations",
          number: state.pdsNumber ?? "✓",
        })}
      />
    );
  }

  // FAILED carries a backend message — scrub it (never surface a raw
  // ClientError/PII dump); fall back to a generic line.
  const failedReason = integrationErrorMessage(
    state.errorMessage,
    t("The export to PDS failed. Please try again.", { ns: "Integrations" })
  );

  const disabledReason = !isPublished
    ? t("Only published quotes can be exported to PDS.", { ns: "Integrations" })
    : state.status === "CUSTOMER_NOT_LINKED"
      ? t("The quote's customer is not linked to PDS.", { ns: "Integrations" })
      : state.status === "PENDING"
        ? t("Export to PDS is running...", { ns: "Integrations" })
        : state.status === "FAILED"
          ? failedReason
          : undefined;

  const isDisabled =
    !isPublished ||
    state.status === "CUSTOMER_NOT_LINKED" ||
    state.status === "PENDING" ||
    exporting ||
    loading;

  const label =
    state.status === "PENDING"
      ? t("Exporting to PDS...", { ns: "Integrations" })
      : state.status === "FAILED"
        ? t("Retry export to PDS", { ns: "Integrations" })
        : t("Export to PDS", { ns: "Integrations" });

  const button = (
    <Button
      size="small"
      variant="outlined"
      color={state.status === "FAILED" ? "error" : "primary"}
      // Navy by default; grey it to match the label in the disabled state
      // (the mark honours currentColor).
      startIcon={
        <PdsIcon
          style={
            isDisabled ? { color: theme.palette.action.disabled } : undefined
          }
        />
      }
      disabled={isDisabled}
      onClick={handleExport}
    >
      {label}
    </Button>
  );

  return disabledReason ? (
    <Tooltip title={disabledReason}>
      <span>{button}</span>
    </Tooltip>
  ) : (
    button
  );
};
